import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CheckCircleIcon,
  MailIcon,
  MinusCircleIcon,
  RefreshIcon,
  XIcon,
} from '@heroicons/react/outline';
import {
  apiHarbourSetSchoolTeacherStatus,
  apiHarbourUpdateSchoolTeacher,
  apiProviderListSchoolTeachers,
  apiProviderResendSchoolTeacherEmail,
} from '../../utils/rmsApi';
import { TEACHER_ROLE_MAX_LENGTH, newTeacherErrors, teacherFullName } from '../../utils/schoolTeachers';
import {
  ALL_SCHOOLS,
  PROVIDER_TEACHER_STATUS,
  filterTeachersBySchool,
  formatSignedIn,
  formsBackToSchoolLine,
  teacherDetailsPayload,
} from '../../utils/providerSchoolTeachers';

/**
 * RmsSchoolTeachersPanel
 * ----------------------
 * The "Teachers" list on the provider Schools page (TDI-621, FR22 to FR24):
 * every school's teacher records, with a School filter. Org Admins can
 * change a teacher's details (the SENCO's rules, including the email
 * confirm), send the first email again before a password is set, mark a
 * teacher as no longer at the school and bring them back. Support Users see
 * the list without the actions, as for SENCO registration.
 *
 * Every change goes through the same callables as the SENCO's, which write
 * it to the teacher record's history with the Org Admin as the actor.
 *
 * No design frame exists for this page; it reuses the Schools page's table,
 * status badges and dialog pattern (RmsSencoRegistrationForm). Diana adds a
 * frame afterwards.
 */

const STATUS_ICONS = { check: CheckCircleIcon, mail: MailIcon, minus: MinusCircleIcon };

const inputClass = (error) => `w-full p-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
  error ? 'border-red-400' : 'border-gray-300'
}`;

function StatusBadge({ status }) {
  const badge = PROVIDER_TEACHER_STATUS[status] || PROVIDER_TEACHER_STATUS.active;
  const Icon = STATUS_ICONS[badge.icon];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-medium ${badge.className}`}>
      {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
      {badge.label}
    </span>
  );
}

function DialogShell({ open, title, description, onClose, busy, children, footer }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden flex flex-col text-left">
          <div className="flex items-start justify-between p-5 pb-0 gap-4">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="text-base text-gray-600 mt-1">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>
          {children && <div className="flex-grow overflow-y-auto p-5 space-y-4">{children}</div>}
          <div className="flex justify-end gap-3 p-5 pt-3 border-t border-gray-100">{footer}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ id, label, hint, error, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-base font-medium text-gray-800">{label}</label>
      {hint && <p id={`${id}-hint`} className="text-sm text-gray-600 mt-0.5">{hint}</p>}
      <input
        id={id}
        className={`${inputClass(error)} mt-1`}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined}
        {...rest}
      />
      {error && <p id={`${id}-error`} className="text-sm text-red-700 mt-1">{error}</p>}
    </div>
  );
}

const cancelButtonClass = 'px-4 py-2 min-h-[44px] text-base font-medium text-primary underline rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';
const primaryButtonClass = 'px-4 py-2 min-h-[44px] bg-primary text-white rounded-lg hover:bg-primary/90 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60';
const rowActionClass = 'text-base font-medium text-primary underline min-h-[44px] px-1 text-left focus:outline-none focus:ring-2 focus:ring-primary rounded disabled:opacity-50';

/** "Change details": the SENCO's Change teacher form, for any school (S5). */
function ChangeDetailsDialog({ orgId, teacher, onClose, onSaved }) {
  const initial = {
    firstName: teacher.firstName || '',
    lastName: teacher.lastName || '',
    email: teacher.email || '',
    jobRole: teacher.jobRole || '',
    phone: teacher.phone || '',
  };
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState('details');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const errors = submitted ? newTeacherErrors(form) : {};
  const first = teacher.firstName || 'this teacher';
  const school = teacher.schoolName || 'the school';
  const newEmail = form.email.trim();
  const emailChanged = newEmail.toLowerCase() !== String(initial.email).trim().toLowerCase();
  const set = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const send = async (confirmEmailChange) => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await apiHarbourUpdateSchoolTeacher({
        orgId, schoolTeacherId: teacher.schoolTeacherId, ...teacherDetailsPayload(form), confirmEmailChange,
      });
      if (res && res.ok === false && res.code === 'ALREADY_ON_SCHOOL_LIST') {
        const holder = teacherFullName(res.teacher || {}) || newEmail;
        setStep('details');
        setFailure(`${holder} is already on ${school}'s list`);
        return;
      }
      onSaved((res && res.teacher) || {});
    } catch (e) {
      setStep('details');
      setFailure(e?.message || "The teacher's details could not be saved. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    setSubmitted(true);
    if (Object.keys(newTeacherErrors(form)).length) return;
    if (emailChanged) {
      setStep('confirmEmail');
      return;
    }
    send(false);
  };

  if (step === 'confirmEmail') {
    return (
      <DialogShell
        open
        title={`Change ${first}'s email address?`}
        onClose={() => setStep('details')}
        busy={busy}
        footer={(
          <>
            <button type="button" className={cancelButtonClass} disabled={busy} onClick={() => setStep('details')}>Cancel</button>
            <button type="button" className={primaryButtonClass} disabled={busy} aria-busy={busy} onClick={() => send(true)}>
              {busy ? 'Saving…' : 'Change email address'}
            </button>
          </>
        )}
      >
        <p className="text-base text-gray-800">
          {`They'll sign in with ${newEmail} from now on. We'll email both addresses to let them know. Any sign-in links already sent to ${initial.email} will stop working.`}
        </p>
      </DialogShell>
    );
  }

  return (
    <DialogShell
      open
      title="Change details"
      description={[teacherFullName(teacher), school].filter(Boolean).join(', ')}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className={cancelButtonClass} disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={busy} aria-busy={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <Field id="teacher-first-name" label="First name" value={form.firstName} onChange={set('firstName')} error={errors.firstName} autoComplete="off" />
      <Field id="teacher-last-name" label="Last name" value={form.lastName} onChange={set('lastName')} error={errors.lastName} autoComplete="off" />
      <Field
        id="teacher-email"
        label="Email address"
        hint="We send their sign-in email here. Use their school email if they have one."
        type="email"
        value={form.email}
        onChange={set('email')}
        error={errors.email}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
      />
      <Field
        id="teacher-role"
        label="Role"
        hint="For example, Year 3 class teacher or maths teacher"
        value={form.jobRole}
        onChange={set('jobRole')}
        error={errors.jobRole}
        maxLength={TEACHER_ROLE_MAX_LENGTH}
        autoComplete="off"
      />
      <Field id="teacher-phone" label="Phone number" hint="Optional" type="tel" value={form.phone} onChange={set('phone')} autoComplete="off" />
      {failure && (
        <div role="alert" className="p-3 text-base text-red-800 bg-red-50 border border-red-200 rounded-lg">{failure}</div>
      )}
    </DialogShell>
  );
}

/** "No longer at this school": S7's confirm, written for the school (B1). */
function LeaveDialog({ orgId, teacher, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const name = teacherFullName(teacher) || teacher.email;
  const school = teacher.schoolName || 'the school';
  const confirm = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const res = await apiHarbourSetSchoolTeacherStatus({
        orgId, schoolTeacherId: teacher.schoolTeacherId, status: 'left',
      });
      onDone(res || {});
    } catch (e) {
      setFailure(e?.message || 'That did not work. Check your connection and try again.');
      setBusy(false);
    }
  };
  return (
    <DialogShell
      open
      title={`${name} no longer works at ${school}?`}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className={cancelButtonClass} disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={busy} aria-busy={busy} onClick={confirm}>
            {busy ? 'Saving…' : "Yes, they've left"}
          </button>
        </>
      )}
    >
      <p className="text-base text-gray-800">
        {`They won't be able to sign in for any of ${school}'s pupils. Forms they haven't finished go back to ${school}'s SENCO, with their answers so far, so they can give them to someone else. Forms they finished stay as they are.`}
      </p>
      {failure && (
        <div role="alert" className="p-3 text-base text-red-800 bg-red-50 border border-red-200 rounded-lg">{failure}</div>
      )}
    </DialogShell>
  );
}

export default function RmsSchoolTeachersPanel({ orgId }) {
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [school, setSchool] = useState(ALL_SCHOOLS);
  const [notice, setNotice] = useState(null); // { kind: 'success' | 'error', title, body? }
  const [changing, setChanging] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await apiProviderListSchoolTeachers({ orgId });
      setTeachers((res && res.teachers) || []);
      setSchools((res && res.schools) || []);
      setCanManage(Boolean(res && res.canManage));
      setLoadError(null);
    } catch (e) {
      setLoadError('The teachers could not be loaded. Refresh the page to try again.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterTeachersBySchool(teachers, school), [teachers, school]);

  const resend = async (t) => {
    setBusyId(t.schoolTeacherId);
    setNotice(null);
    try {
      await apiProviderResendSchoolTeacherEmail({ orgId, schoolTeacherId: t.schoolTeacherId });
      setNotice({ kind: 'success', title: `We've emailed ${t.firstName || teacherFullName(t)} again.` });
      await load();
    } catch (e) {
      setNotice({ kind: 'error', title: 'That did not work', body: e?.message || 'The email could not be sent. Try again in a few minutes.' });
    } finally {
      setBusyId(null);
    }
  };

  const bringBack = async (t) => {
    setBusyId(t.schoolTeacherId);
    setNotice(null);
    try {
      await apiHarbourSetSchoolTeacherStatus({ orgId, schoolTeacherId: t.schoolTeacherId, status: 'active' });
      setNotice({ kind: 'success', title: `${teacherFullName(t)} is back on ${t.schoolName || 'the school'}'s list.` });
      await load();
    } catch (e) {
      setNotice({ kind: 'error', title: 'That did not work', body: e?.message || 'Check your connection and try again.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-labelledby="school-teachers-heading" className="mt-10 text-left">
      <h2 id="school-teachers-heading" className="text-xl font-bold text-gray-900">Teachers</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4 mb-6">
        <label htmlFor="school-teachers-filter" className="block text-base font-medium text-gray-800">School</label>
        <select
          id="school-teachers-filter"
          value={school}
          onChange={(e) => setSchool(e.target.value)}
          className="mt-1 w-full sm:w-80 p-2.5 min-h-[44px] border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value={ALL_SCHOOLS}>All schools</option>
          {schools.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {notice && (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={`mb-4 p-3 rounded-lg border text-base flex items-start justify-between gap-3 ${
            notice.kind === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'
          }`}
        >
          <div>
            <p className="font-semibold">{notice.title}</p>
            {notice.body && <p>{notice.body}</p>}
          </div>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Dismiss"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" aria-hidden="true" />
          <p className="text-gray-700">Loading teachers...</p>
        </div>
      )}

      {!loading && loadError && (
        <div role="alert" className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
          <p className="text-red-800">{loadError}</p>
        </div>
      )}

      {!loading && !loadError && shown.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-900 font-medium mb-1">No teachers yet</p>
          <p className="text-base text-gray-700">SENCOs add teachers from a pupil&apos;s record. They appear here once added.</p>
        </div>
      )}

      {!loading && !loadError && shown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Role', 'Email', 'School', 'Pupils', 'Status', 'Last signed in'].map((h) => (
                  <th key={h} scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{h}</th>
                ))}
                {canManage && (
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {shown.map((t) => {
                const name = teacherFullName(t) || t.email;
                const busy = busyId === t.schoolTeacherId;
                return (
                  <tr key={t.schoolTeacherId}>
                    <td className="px-4 py-3 text-base text-gray-900 whitespace-nowrap">{name}</td>
                    <td className="px-4 py-3 text-base text-gray-700">{t.jobRole || 'Role not added yet'}</td>
                    <td className="px-4 py-3 text-base text-gray-700 whitespace-nowrap">{t.email}</td>
                    <td className="px-4 py-3 text-base text-gray-700">{t.schoolName || '-'}</td>
                    <td className="px-4 py-3 text-base text-gray-700">{t.pupilCount}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={t.providerStatus} /></td>
                    <td className="px-4 py-3 text-base text-gray-700 whitespace-nowrap">{formatSignedIn(t.lastSignedInAt) || 'Not yet'}</td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <button type="button" className={rowActionClass} disabled={busy} onClick={() => { setNotice(null); setChanging(t); }} aria-label={`Change details for ${name}`}>
                            Change details
                          </button>
                          {t.canResend && (
                            <button type="button" className={rowActionClass} disabled={busy} onClick={() => resend(t)} aria-label={`Send ${name} the email again`}>
                              {busy ? 'Sending…' : 'Send the email again'}
                            </button>
                          )}
                          {t.status === 'left' ? (
                            <button type="button" className={rowActionClass} disabled={busy} onClick={() => bringBack(t)} aria-label={`Bring back ${name}`}>
                              Bring back
                            </button>
                          ) : (
                            <button type="button" className={rowActionClass} disabled={busy} onClick={() => { setNotice(null); setLeaving(t); }} aria-label={`${name} is no longer at this school`}>
                              No longer at this school
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {changing && (
        <ChangeDetailsDialog
          orgId={orgId}
          teacher={changing}
          onClose={() => setChanging(null)}
          onSaved={(saved) => {
            const name = teacherFullName(saved) || teacherFullName(changing);
            setChanging(null);
            setNotice({ kind: 'success', title: `${name}'s details are saved.` });
            load();
          }}
        />
      )}

      {leaving && (
        <LeaveDialog
          orgId={orgId}
          teacher={leaving}
          onClose={() => setLeaving(null)}
          onDone={(res) => {
            const name = teacherFullName(leaving);
            const where = leaving.schoolName || 'the school';
            setLeaving(null);
            setNotice({
              kind: 'success',
              title: `${name} has been taken off ${where}'s list.`,
              body: formsBackToSchoolLine((res && res.unfinishedCount) || 0, leaving.schoolName),
            });
            load();
          }}
        />
      )}
    </section>
  );
}
